"use client";

import React, { useState, useCallback, useMemo } from "react";
import type { Category, ComboV3, Product } from "@/src/types/tenant";

export interface FilterState {
  category: string;        // category _id, or "all"
  subcategory: string;     // subcategory _id, or "all"
  curators: string[];      // product/combo tag values
  priceRanges: string[];   // "under-300" | "300-600" | "600-1000" | "above-1000"
  /**
   * Explicit product membership for the selected category, used when the
   * selection came from the CANONICAL taxonomy rather than a portal-api id.
   *
   * Needed because the landing payload carries neither our category ids nor
   * `taxonomy`, so a canonical node like "Nut Chocolate" — which portal-api has
   * never heard of — cannot be matched by id. The product service supplies the
   * membership instead. Absent means fall back to legacy id matching.
   */
  categoryProductIds?: string[];
}

interface SidebarFilterProps {
  categories?: Category[];
  products?: Product[];
  combos?: ComboV3[];
  onFilterChange?: (filters: FilterState) => void;
}

const PRICE_RANGES = [
  { id: "under-300",   label: "Under ₹300" },
  { id: "300-600",     label: "₹300 – ₹600" },
  { id: "600-1000",    label: "₹600 – ₹1,000" },
  { id: "above-1000",  label: "Above ₹1,000" },
];

const CURATORS = [
  { id: "bestsellers", label: "Bestsellers" },
  { id: "new",         label: "New arrivals" },
  { id: "gifting",     label: "Gifting" },
  { id: "sale",        label: "On sale" },
];

export const SidebarFilter: React.FC<SidebarFilterProps> = ({
  categories = [],
  products = [],
  combos = [],
  onFilterChange,
}) => {
  const [activeCategory, setActiveCategory] = useState("all");
  const [activeSubcategory, setActiveSubcategory] = useState("all");
  const [curators, setCurators] = useState<string[]>([]);
  const [priceRanges, setPriceRanges] = useState<string[]>([]);

  const emit = useCallback(
    (cat: string, sub: string, cur: string[], prices: string[]) => {
      onFilterChange?.({ category: cat, subcategory: sub, curators: cur, priceRanges: prices });
    },
    [onFilterChange]
  );

  const handleCategory = (id: string) => {
    setActiveCategory(id);
    setActiveSubcategory("all");
    emit(id, "all", curators, priceRanges);
  };

  const handleSubcategory = (catId: string, subId: string) => {
    setActiveCategory(catId);
    setActiveSubcategory(subId);
    emit(catId, subId, curators, priceRanges);
  };

  const toggleCurator = (id: string) => {
    const next = curators.includes(id)
      ? curators.filter((c) => c !== id)
      : [...curators, id];
    setCurators(next);
    emit(activeCategory, activeSubcategory, next, priceRanges);
  };

  const togglePrice = (id: string) => {
    const next = priceRanges.includes(id)
      ? priceRanges.filter((p) => p !== id)
      : [...priceRanges, id];
    setPriceRanges(next);
    emit(activeCategory, activeSubcategory, curators, next);
  };

  // Unique tags from products + combos for the Curator's pick filter
  const availableTags = useMemo(() => {
    const set = new Set<string>();
    products.forEach((p) => p.tags?.forEach((t) => t && set.add(t)));
    combos.forEach((c) => c.tag && set.add(c.tag));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [products, combos]);

  const curatorItems = useMemo(
    () =>
      availableTags.length > 0
        ? availableTags.map((t) => ({ id: t, label: t }))
        : CURATORS,
    [availableTags]
  );

  // Build subcategory map: categoryId → deduplicated Category[]
  const subcategoryMap = useMemo(() => {
    const map = new Map<string, Map<string, Category>>();
    products.forEach((p) => {
      if (!p.category || !p.subcategories?.length) return;
      const catId = p.category._id;
      if (!map.has(catId)) map.set(catId, new Map());
      p.subcategories.forEach((sc) => map.get(catId)!.set(sc._id, sc));
    });
    return map;
  }, [products]);

  const getCategoryCount = (catId: string) =>
    products.filter((p) => catId === "all" || p.category?._id === catId).length;

  const getSubcategoryCount = (catId: string, subId: string) =>
    products.filter(
      (p) => p.category?._id === catId && p.subcategories?.some((sc) => sc._id === subId)
    ).length;

  const categoryOptions = [
    { id: "all", label: "All Products", count: products.length },
    ...categories.map((c) => ({ id: c._id, label: c.name, count: getCategoryCount(c._id) })),
  ];

  return (
    <aside
      className="hidden md:flex md:flex-col"
      style={{ position: "sticky", top: "calc(64px + 16px)", gap: 32 }}
    >
      {/* ── Categories ── */}
      <div>
        <div
          className="mb-4 font-bold uppercase tracking-[1.8px]"
          style={{ fontSize: 11, color: "var(--techno-black-50)" }}
        >
          Categories
        </div>
        <ul className="flex flex-col gap-0.5">
          {categoryOptions.map((cat) => {
            const isActive = activeCategory === cat.id;
            const subcats = cat.id !== "all" ? Array.from(subcategoryMap.get(cat.id)?.values() ?? []) : [];
            const hasSubcats = subcats.length > 0;

            return (
              <li key={cat.id}>
                {/* Category row */}
                <button
                  onClick={() => handleCategory(cat.id)}
                  className="flex w-full items-center justify-between rounded-[10px] px-3.5 py-2.5 text-sm transition"
                  style={{
                    background: isActive && activeSubcategory === "all" ? "#F26250" : "transparent",
                    color: isActive && activeSubcategory === "all" ? "#fff" : "var(--techno-black-70)",
                    fontWeight: isActive ? 600 : 500,
                  }}
                  onMouseEnter={(e) => {
                    if (!(isActive && activeSubcategory === "all"))
                      e.currentTarget.style.background = "var(--techno-black-10)";
                  }}
                  onMouseLeave={(e) => {
                    if (!(isActive && activeSubcategory === "all"))
                      e.currentTarget.style.background = "transparent";
                  }}
                >
                  <span className="flex items-center gap-1.5">
                    {hasSubcats && (
                      <svg
                        width="10" height="10" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                        style={{ opacity: 0.5, transform: isActive ? "rotate(90deg)" : "none", transition: "transform 180ms" }}
                      >
                        <polyline points="9 18 15 12 9 6"/>
                      </svg>
                    )}
                    {cat.label}
                  </span>
                  <span style={{ fontSize: 12, opacity: 0.6 }}>{cat.count}</span>
                </button>

                {/* Subcategory rows — visible when parent is active */}
                {isActive && hasSubcats && (
                  <ul className="mt-0.5 flex flex-col gap-0.5 pl-4">
                    {/* "All [category]" sub-row */}
                    <li>
                      <button
                        onClick={() => handleSubcategory(cat.id, "all")}
                        className="flex w-full items-center justify-between rounded-[8px] px-3 py-2 text-[13px] transition"
                        style={{
                          background: activeSubcategory === "all" ? "rgba(242, 98, 80,0.1)" : "transparent",
                          color: activeSubcategory === "all" ? "#F26250" : "var(--techno-black-50)",
                          fontWeight: activeSubcategory === "all" ? 600 : 400,
                        }}
                        onMouseEnter={(e) => {
                          if (activeSubcategory !== "all")
                            e.currentTarget.style.background = "var(--techno-black-10)";
                        }}
                        onMouseLeave={(e) => {
                          if (activeSubcategory !== "all")
                            e.currentTarget.style.background = "transparent";
                        }}
                      >
                        <span>All {cat.label}</span>
                        <span style={{ fontSize: 11, opacity: 0.5 }}>{cat.count}</span>
                      </button>
                    </li>

                    {subcats.map((sc) => {
                      const isSubActive = activeSubcategory === sc._id;
                      const subCount = getSubcategoryCount(cat.id, sc._id);
                      return (
                        <li key={sc._id}>
                          <button
                            onClick={() => handleSubcategory(cat.id, sc._id)}
                            className="flex w-full items-center justify-between rounded-[8px] px-3 py-2 text-[13px] transition"
                            style={{
                              background: isSubActive ? "rgba(242, 98, 80,0.1)" : "transparent",
                              color: isSubActive ? "#F26250" : "var(--techno-black-50)",
                              fontWeight: isSubActive ? 600 : 400,
                            }}
                            onMouseEnter={(e) => {
                              if (!isSubActive)
                                e.currentTarget.style.background = "var(--techno-black-10)";
                            }}
                            onMouseLeave={(e) => {
                              if (!isSubActive)
                                e.currentTarget.style.background = "transparent";
                            }}
                          >
                            <span className="flex items-center gap-1.5">
                              <span style={{ width: 4, height: 4, borderRadius: "50%", background: "currentColor", display: "inline-block", opacity: 0.5, flexShrink: 0 }} />
                              {sc.name}
                            </span>
                            <span style={{ fontSize: 11, opacity: 0.5 }}>{subCount}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </div>

      {/* ── Curator's pick ── */}
      {curatorItems.length > 0 && (
        <div>
          <div
            className="mb-4 font-bold uppercase tracking-[1.8px]"
            style={{ fontSize: 11, color: "var(--techno-black-50)" }}
          >
            Curator&apos;s pick
          </div>
          <div
            className="flex flex-col gap-0.5 overflow-y-auto pr-1"
            style={{ maxHeight: 220 }}
          >
            {curatorItems.map((c) => (
              <label
                key={c.id}
                className="flex cursor-pointer items-center gap-2.5 rounded-[10px] px-3.5 py-2.5 text-sm transition"
                style={{ color: "var(--techno-black-70)" }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.color = "var(--techno-black)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.color = "var(--techno-black-70)")
                }
              >
                <input
                  type="checkbox"
                  checked={curators.includes(c.id)}
                  onChange={() => toggleCurator(c.id)}
                  className="h-4 w-4 cursor-pointer"
                  style={{ accentColor: "#F26250" }}
                />
                {c.label}
              </label>
            ))}
          </div>
        </div>
      )}

      {/* ── Price ── */}
      <div>
        <div
          className="mb-4 font-bold uppercase tracking-[1.8px]"
          style={{ fontSize: 11, color: "var(--techno-black-50)" }}
        >
          Price
        </div>
        <div className="flex flex-col gap-0.5">
          {PRICE_RANGES.map((r) => (
            <label
              key={r.id}
              className="flex cursor-pointer items-center gap-2.5 rounded-[10px] px-3.5 py-2.5 text-sm transition"
              style={{ color: "var(--techno-black-70)" }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.color = "var(--techno-black)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.color = "var(--techno-black-70)")
              }
            >
              <input
                type="checkbox"
                checked={priceRanges.includes(r.id)}
                onChange={() => togglePrice(r.id)}
                className="h-4 w-4 cursor-pointer"
                style={{ accentColor: "#F26250" }}
              />
              {r.label}
            </label>
          ))}
        </div>
      </div>
    </aside>
  );
};